import React, { useState, type FormEvent } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Link as MuiLink, Stack, TextField, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';

export const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { login, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!username || !password) {
      setError('Username and password are required.');
      return;
    }
    try {
      await login({ username, password });
    } catch (err: unknown) {
      console.error('Login page error:', err);
      const message = err instanceof Error ? err.message : 'Failed to log in. Check your credentials.';
      setError(message);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 2, md: 6 } }}>
      <Card sx={{ width: '100%', maxWidth: 440 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img src="/images/DIRTcarLogo.png" alt="DIRTcar" style={{ height: 48, width: 'auto' }} />
          </Box>
          <Typography variant="h5" align="center" sx={{ fontWeight: 600, mb: 0.5 }}>
            Sign in
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Manage points, standings, and reports.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
                autoComplete="username"
                autoFocus
                required
                fullWidth
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="current-password"
                required
                fullWidth
              />
              <Button type="submit" variant="contained" size="large" disabled={isLoading} fullWidth>
                {isLoading ? 'Signing in' : 'Sign in'}
              </Button>
            </Stack>
          </Box>

          <Stack direction="row" justifyContent="space-between" sx={{ mt: 2.5 }}>
            <MuiLink component={RouterLink} to="/register" variant="body2" underline="hover">
              Create account
            </MuiLink>
            <MuiLink component={RouterLink} to="/forgot-password" variant="body2" underline="hover">
              Forgot password?
            </MuiLink>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
